(() => {
  /* OXICLEAR BAND PALETTE (OS module 13 section 3.4).
     The upstream index publishes raw sRGB primaries for its bands: Moderate
     arrives as #FFFF00, Very Poor as #FF0000. Worn at 86% across the whole
     Pulse Card those are somebody else's colours, and they are why the card
     read wrong. Every band colour in this widget already passes through
     safeColor(), so the translation happens once, there. Anything not in this
     table passes through untouched, so a new scale or a new upstream colour
     still renders, just in its own hex.
     Founder-approved 26 August 2026. This is a PALETTE, not a data change: the
     index, the band, the label and the advice all remain the upstream's. */
  const OXI_BAND_PALETTE = {
    '#00b050': { key: 'good',         hex: '#1B9E5A' },
    '#92d050': { key: 'satisfactory', hex: '#7FC243' },
    '#ffff00': { key: 'moderate',     hex: '#F2C230' },
    '#ff6500': { key: 'poor',         hex: '#F08A24' },
    '#ff0000': { key: 'very-poor',    hex: '#E03A3A' },
    '#c00000': { key: 'severe',       hex: '#8B1E2D' }
  };

  class AirCheckController {
    constructor(root) {
      this.root = root;
      this.endpoint = root.dataset.endpoint;
      this.place = root.querySelector('[data-air-place]');
      this.region = root.querySelector('[data-air-region]');
      this.locationSource = root.querySelector('[data-air-location-source]');
      this.indexName = root.querySelector('[data-air-index-name]');
      this.indexValue = root.querySelector('[data-air-index]');
      this.status = root.querySelector('[data-air-status]');
      this.meaning = root.querySelector('[data-air-meaning]');
      this.updated = root.querySelector('[data-air-updated]');
      this.updatedLabel = root.querySelector('[data-air-updated-label]');
      this.updatedDefaultLabel = this.updatedLabel.textContent;
      this.dominantName = root.querySelector('[data-air-dominant-name]');
      this.dominantValue = root.querySelector('[data-air-dominant-value]');
      this.dominantUnit = root.querySelector('[data-air-dominant-unit]');
      this.scale = root.querySelector('[data-air-scale]');
      this.scaleName = root.querySelector('[data-air-scale-name]');
      this.scaleCurrent = root.querySelector('[data-air-scale-current]');
      this.scaleTrack = root.querySelector('[data-air-scale-track]');
      this.scaleMarker = root.querySelector('[data-air-marker]');
      this.scaleMin = root.querySelector('[data-air-scale-min]');
      this.scaleMax = root.querySelector('[data-air-scale-max]');
      this.preciseButton = root.querySelector('[data-air-precise]');
      this.preciseAction = root.querySelector('[data-air-precise-action]');
      this.refreshButton = root.querySelector('[data-air-refresh]');
      this.searchPanel = root.querySelector('[data-air-search-panel]');
      this.searchForm = root.querySelector('[data-air-search]');
      this.cityInput = root.querySelector('[data-air-city]');
      this.clearButton = root.querySelector('[data-air-clear]');
      this.suggestionsWrap = root.querySelector('[data-air-suggestions-wrap]');
      this.suggestionsList = root.querySelector('[data-air-suggestions]');
      this.notice = root.querySelector('[data-air-notice]');
      this.live = root.querySelector('[data-air-live]');
      this.locationPrompt = root.querySelector('[data-air-location-prompt]');
      this.locationPromptCopy = root.querySelector('[data-air-location-prompt-copy]');
      this.locationDismiss = root.querySelector('[data-air-location-dismiss]');
      this.favoriteButton = root.querySelector('[data-air-favorite-save]');
      this.favoriteButtonLabel = root.querySelector('[data-air-favorite-save-label]');
      this.favoriteCount = root.querySelector('[data-air-favorite-count]');
      this.favoriteList = root.querySelector('[data-air-favorite-list]');
      this.favoriteEmpty = root.querySelector('[data-air-favorite-empty]');
      this.hourlyList = root.querySelector('[data-air-hourly-list]');
      this.hourlyState = root.querySelector('[data-air-hourly-state]');
      this.hourlyCurve = root.querySelector('[data-air-hourly-curve]');
      this.pastList = root.querySelector('[data-air-past-list]');
      this.forecastList = root.querySelector('[data-air-forecast-list]');
      this.historyContent = root.querySelector('[data-air-history-content]');
      this.historyState = root.querySelector('[data-air-history-state]');
      this.historyMethod = root.querySelector('[data-air-history-method]');
      this.pollutants = root.querySelector('[data-air-pollutants]');
      this.pollenSummary = root.querySelector('[data-air-pollen-summary]');
      this.pollenSection = root.querySelector('[data-air-pollen-section]');
      this.pollenForecast = root.querySelector('[data-air-pollen-forecast]');
      this.pollenDays = root.querySelector('[data-air-pollen-days]');
      this.pollenAttribution = root.querySelector('[data-air-pollen-attribution]');
      this.pollenAttributionSeparator = root.querySelector('[data-air-pollen-attribution-separator]');
      this.airAttribution = root.querySelector('[data-air-air-attribution]');
      this.tabList = root.querySelector('[data-air-tabs]');
      this.tabs = Array.from(root.querySelectorAll('[data-air-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-air-panel]'));
      this.onTabClick = (event) => this.handleTabClick(event);
      this.onTabKeydown = (event) => this.handleTabKeydown(event);
      this.activeRequest = null;
      this.lastData = null;
      this.requestSequence = 0;
      this.locationIntentSequence = 0;
      this.preciseTargetMeters = 500;
      this.inflight = new Map();
      this.resultCache = new Map();
      this.suggestions = [];
      this.activeSuggestion = -1;
      this.suggestionTimer = null;
      this.suggestionController = null;
      this.dataController = null;
      this.preciseAcquisitionCancel = null;
      this.sessionToken = this.newSessionToken();
      this.numberFrame = null;
      this.permissionState = 'unknown';
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.savedPlaceKey = 'oxiclear-air-check-place-id';
      this.favoritesKey = 'oxiclear-air-check-favorites-v1';
      this.activeFavoriteKey = 'oxiclear-air-check-active-favorite-v1';
      this.promptChoiceKey = 'oxiclear-air-check-location-choice-v1';
      this.readingKey = 'oxiclear-air-reading-v1';
      this.analyticsVisitorKey = 'oxiclear-air-check-analytics-id-v1';
      this.analyticsEnabled = !this.shouldSkipAnalytics();
      this.analyticsAllowed = false;
      this.analyticsConsentReady = false;
      this.analyticsWidgetReadySent = false;
      this.analyticsControllers = new Set();
      this.analyticsDestroyed = false;
      this.onVisitorConsentCollected = () => this.syncAnalyticsConsent();
      this.favorites = this.readFavorites();
      this.currentLocation = null;
      this.initializeAnalytics();
      this.bind();
      this.activateTab('now');
      this.renderFavorites();
      this.initialize();
    }

    bind() {
      this.preciseButton.addEventListener('click', () => this.usePreciseLocation(true));
      this.refreshButton.addEventListener('click', () => this.refresh());
      this.locationDismiss.addEventListener('click', () => this.dismissLocationPrompt('not-now'));
      this.favoriteButton.addEventListener('click', () => this.saveCurrentFavorite());
      this.favoriteList.addEventListener('click', (event) => this.onFavoriteClick(event));
      this.clearButton.addEventListener('click', () => this.clearSearch());
      this.searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.activeSuggestion >= 0) {
          this.selectSuggestion(this.activeSuggestion);
        } else {
          this.cityInput.setAttribute('aria-invalid', 'true');
          this.setNotice(this.root.dataset.copyInvalidSelection);
        }
      });
      this.cityInput.addEventListener('input', () => this.onSearchInput());
      this.cityInput.addEventListener('keydown', (event) => this.onSearchKeydown(event));
      this.searchPanel.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.searchPanel.contains(document.activeElement)) this.hideSuggestions();
        }, 0);
      });
      this.suggestionsList.addEventListener('mousedown', (event) => event.preventDefault());
      this.suggestionsList.addEventListener('click', (event) => {
        const option = event.target.closest('[data-air-option]');
        if (!option) return;
        this.selectSuggestion(Number(option.dataset.index));
      });
      this.tabList.addEventListener('click', this.onTabClick);
      this.tabList.addEventListener('keydown', this.onTabKeydown);
    }

    handleTabClick(event) {
      const tab = event.target.closest('[data-air-tab]');
      if (!tab || !this.tabList.contains(tab)) return;
      this.activateTab(tab.dataset.airTab);
    }

    handleTabKeydown(event) {
      const tab = event.target.closest('[data-air-tab]');
      if (!tab || !this.tabList.contains(tab)) return;
      const currentIndex = this.tabs.indexOf(tab);
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }
      event.preventDefault();
      const nextTab = this.tabs[nextIndex];
      this.activateTab(nextTab.dataset.airTab, true);
    }

    activateTab(name, moveFocus = false) {
      const nextTab = this.tabs.find((tab) => tab.dataset.airTab === name);
      const nextPanel = this.panels.find((panel) => panel.dataset.airPanel === name);
      if (!nextTab || !nextPanel) return;
      this.tabs.forEach((tab) => {
        const selected = tab === nextTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const active = panel === nextPanel;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      this.root.dataset.activeView = name;
      if (moveFocus) nextTab.focus();
    }

    async initialize() {
      const intent = this.locationIntentSequence;
      const legacyPlaceId = this.readStorage(this.savedPlaceKey);
      const preferredPlaceId = this.preferredFavoriteId() || legacyPlaceId;
      this.updateLocationPrompt();
      if (this.validPlaceId(preferredPlaceId)) {
        const loaded = await this.loadReading(
          { type: 'selected', placeId: preferredPlaceId },
          { message: this.root.dataset.copyLoadingSelected, fallback: true }
        );
        if (intent !== this.locationIntentSequence) return;
        if (loaded) {
          if (preferredPlaceId === legacyPlaceId) {
            this.addFavoriteFromLocation(this.lastData && this.lastData.location, false);
            this.removeStorage(this.savedPlaceKey);
          }
          return;
        }
        if (preferredPlaceId === legacyPlaceId) this.removeStorage(this.savedPlaceKey);
        this.setNotice(this.root.dataset.copySavedUnavailable);
      }
      if (intent !== this.locationIntentSequence) return;
      /* A visitor who chose precise location keeps it (module 13 §7.1: a saved place,
         else device location with consent, else IP city). Until 24 Sep 2026 every
         reload fell back to the IP city, and the header dock followed it sitewide.
         usePreciseLocation(false) goes no further than a permission that is already
         'granted', so this can never raise a prompt (§7.8: never a second dialogue). */
      if (this.readStorage(this.promptChoiceKey) === 'precise' && await this.usePreciseLocation(false)) return;
      if (intent !== this.locationIntentSequence) return;
      await this.loadReading(
        { type: 'approximate' },
        { message: this.root.dataset.copyLoadingApproximate }
      );
    }

    async readPermission() {
      if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        this.permissionState = result.state;
        result.addEventListener('change', () => {
          this.permissionState = result.state;
        });
        return result.state;
      } catch {
        return 'unknown';
      }
    }

    beginLocationIntent() {
      const intent = ++this.locationIntentSequence;
      this.cancelPreciseAcquisition();
      ++this.requestSequence;
      if (this.dataController) this.dataController.abort();
      return intent;
    }

    cancelPreciseAcquisition() {
      if (!this.preciseAcquisitionCancel) return;
      const cancel = this.preciseAcquisitionCancel;
      this.preciseAcquisitionCancel = null;
      cancel();
    }

    positionAccuracy(position) {
      const accuracy = Number(position && position.coords && position.coords.accuracy);
      return Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : Infinity;
    }

    insufficientAccuracyError(position) {
      const error = new Error('Device location accuracy is insufficient.');
      error.code = 'INSUFFICIENT_ACCURACY';
      error.accuracy = this.positionAccuracy(position);
      return error;
    }

    acquirePrecisePosition() {
      const options = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      };
      return new Promise((resolve, reject) => {
        let bestPosition = null;
        let watchId = null;
        let timerId = null;
        let settled = false;
        let watching = false;
        let cancel = null;
        const finish = (callback, value) => {
          if (settled) return;
          settled = true;
          if (timerId !== null) window.clearTimeout(timerId);
          if (watchId !== null && navigator.geolocation.clearWatch) {
            navigator.geolocation.clearWatch(watchId);
          }
          if (this.preciseAcquisitionCancel === cancel) this.preciseAcquisitionCancel = null;
          callback(value);
        };
        cancel = () => {
          const error = new Error('Device location request cancelled.');
          error.name = 'AbortError';
          finish(reject, error);
        };
        this.preciseAcquisitionCancel = cancel;
        const onPosition = (position) => {
          const accuracy = this.positionAccuracy(position);
          if (!bestPosition || accuracy < this.positionAccuracy(bestPosition)) bestPosition = position;
          if (accuracy <= this.preciseTargetMeters) finish(resolve, position);
        };
        const onError = (error) => {
          if (Number(error && error.code) === 1) {
            finish(reject, error);
            return;
          }
          if (watching) return;
          if (bestPosition) {
            finish(reject, this.insufficientAccuracyError(bestPosition));
            return;
          }
          finish(reject, error);
        };

        timerId = window.setTimeout(() => {
          if (bestPosition && this.positionAccuracy(bestPosition) <= this.preciseTargetMeters) {
            finish(resolve, bestPosition);
          } else if (bestPosition) {
            finish(reject, this.insufficientAccuracyError(bestPosition));
          } else {
            const error = new Error('Device location timed out.');
            error.code = 'TIMEOUT';
            finish(reject, error);
          }
        }, 16000);

        try {
          if (navigator.geolocation.watchPosition) {
            watching = true;
            watchId = navigator.geolocation.watchPosition(onPosition, onError, options);
          } else {
            navigator.geolocation.getCurrentPosition((position) => {
              if (this.positionAccuracy(position) <= this.preciseTargetMeters) {
                finish(resolve, position);
              } else {
                finish(reject, this.insufficientAccuracyError(position));
              }
            }, onError, options);
          }
        } catch (error) {
          finish(reject, error);
        }
      });
    }

    showPrecisePending() {
      this.locationSource.textContent = this.root.dataset.copyLocationSourceLocating;
      this.preciseAction.textContent = this.root.dataset.labelPreciseLocating;
    }

    restoreLocationPresentation() {
      if (this.lastData && this.lastData.location) {
        this.renderLocationPresentation(this.lastData.location);
        return;
      }
      this.renderLocationPresentation({ source: 'approximate' });
    }

    async usePreciseLocation(fromUser) {
      if (!navigator.geolocation) {
        if (fromUser) {
          this.setNotice(this.root.dataset.copyPreciseUnavailable);
          this.dismissLocationPrompt('unavailable');
        }
        return false;
      }

      const intent = fromUser ? this.beginLocationIntent() : this.locationIntentSequence;

      if (!fromUser) {
        const permission = await this.readPermission();
        if (intent !== this.locationIntentSequence) return false;
        if (permission !== 'granted') return false;
      }

      if (fromUser) {
        this.showPrecisePending();
        this.setNotice(this.root.dataset.copyPreciseRefining);
        this.preciseButton.disabled = true;
        this.preciseButton.setAttribute('aria-busy', 'true');
      }
      let position;
      try {
        position = await this.acquirePrecisePosition();
      } catch (error) {
        if (intent !== this.locationIntentSequence) return false;
        if (fromUser) {
          this.restoreLocationPresentation();
          const denied = Number(error && error.code) === 1;
          const accuracyLow = error && error.code === 'INSUFFICIENT_ACCURACY';
          const distance = this.formatAccuracy(error && error.accuracy);
          const message = accuracyLow
            ? this.template(this.root.dataset.copyPreciseAccuracyLow, distance)
            : denied
              ? this.root.dataset.copyLocationDeniedHelp
              : this.root.dataset.copyPreciseUnavailable;
          if (this.lastData) {
            this.setBusy(false, 'ready');
            this.setNotice(message);
          } else {
            const fallbackLoaded = await this.loadReading(
              { type: 'approximate' },
              { message: this.root.dataset.copyLoadingApproximate }
            );
            if (intent !== this.locationIntentSequence) return false;
            if (fallbackLoaded) this.setNotice(message);
          }
          this.dismissLocationPrompt(denied ? 'denied' : 'unavailable');
        }
        return false;
      } finally {
        if (fromUser && intent === this.locationIntentSequence) {
          this.preciseButton.disabled = false;
          this.preciseButton.removeAttribute('aria-busy');
        }
      }

      if (intent !== this.locationIntentSequence) return false;
      const accuracy = Math.round(this.positionAccuracy(position));

      const request = {
        type: 'precise',
        lat: position.coords.latitude,
        lon: position.coords.longitude,
        accuracy,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      };
      const loaded = await this.loadReading(request, {
        message: this.root.dataset.copyLoadingPrecise,
        bypass: true,
        expectedSource: 'precise',
        retainDuringLoad: Boolean(this.lastData),
        retainOnFailure: Boolean(this.lastData)
      });
      if (intent !== this.locationIntentSequence) return false;
      if (loaded) {
        this.removeStorage(this.savedPlaceKey);
        this.setNotice(this.template(this.root.dataset.copyPreciseReady, this.formatAccuracy(accuracy)));
        this.dismissLocationPrompt('precise');
      } else if (fromUser && this.lastData) {
        this.restoreLocationPresentation();
        this.setNotice(this.root.dataset.copyPreciseFailedRetained);
      }
      return loaded;
    }

    requestParams(request) {
      if (request.type === 'selected') {
        return {
          hourly: '1',
          placeId: request.placeId,
          ...(request.sessionToken ? { sessionToken: request.sessionToken } : {})
        };
      }
      if (request.type === 'precise') {
        return {
          hourly: '1',
          lat: String(request.lat),
          lon: String(request.lon),
          source: 'precise',
          ...(Number.isFinite(Number(request.accuracy)) ? { accuracy: String(Math.round(Number(request.accuracy))) } : {}),
          ...(request.timeZone ? { timeZone: request.timeZone } : {})
        };
      }
      return { hourly: '1' };
    }

    async loadReading(request, options = {}) {
      const params = this.requestParams(request);
      const sequence = ++this.requestSequence;
      const retainDuringLoad = Boolean(options.retainDuringLoad && this.lastData);
      if (!retainDuringLoad) this.prepareLoading(request);
      this.setBusy(true, retainDuringLoad ? 'refreshing' : 'loading');
      this.setNotice(options.message || this.root.dataset.copyLoadingNew);

      try {
        const data = await this.fetchReading(params, Boolean(options.bypass));
        if (sequence !== this.requestSequence) return false;
        if (options.expectedSource && (!data.location || data.location.source !== options.expectedSource)) {
          const error = new Error('Location source did not match the request.');
          error.code = 'LOCATION_SOURCE_MISMATCH';
          throw error;
        }
        this.render(data);
        this.activeRequest = request.type === 'selected'
          ? { type: 'selected', placeId: request.placeId }
          : { ...request };
        this.lastData = data;
        this.publishReading(data);
        this.trackWidgetReadyAnalytics();
        this.setBusy(false, 'ready');
        this.setNotice(this.root.dataset.copyReady);
        this.refreshButton.hidden = false;
        if (request.type === 'selected' && this.favorites.some((item) => item.placeId === request.placeId)) {
          this.writeStorage(this.activeFavoriteKey, request.placeId);
          this.renderFavorites();
        }
        this.announce(
          (data.location && data.location.label ? data.location.label + '. ' : '') +
          String(
            data.current && data.current.display !== undefined && data.current.display !== null
              ? data.current.display
              : ''
          ) +
          (data.current && data.current.status ? ', ' + data.current.status : '')
        );
        return true;
      } catch (error) {
        if (error && error.name === 'AbortError') return false;
        if (sequence !== this.requestSequence) return false;
        if (options.fallback) {
          this.setBusy(false, this.lastData ? 'ready' : 'error');
          return false;
        }
        if (options.retainOnFailure && this.lastData) {
          this.setBusy(false, 'ready');
          this.setNotice(this.root.dataset.copyServiceUnavailable);
          return false;
        }
        if (options.refresh && this.lastData) {
          this.setBusy(false, 'stale');
          this.updatedLabel.textContent = this.root.dataset.copyLastAvailable;
          this.setNotice(this.root.dataset.copyRefreshFailed);
          return false;
        }
        this.showError(error && error.code === 'LOCATION_UNAVAILABLE'
          ? this.root.dataset.copyLocationUnavailable
          : this.root.dataset.copyServiceUnavailable);
        return false;
      }
    }

    /* THE PUBLISHED READING (24 Sep 2026). Every reading this card settles, precise,
       saved place or IP city, becomes the one reading the rest of the site prints:
       the header dock (snippets/oxiclear-air-teaser.liquid) and the PDP AQI cell read
       it on every page and follow this event. Before this, a page with no card asked
       the service, which only knows the IP city, so the dock snapped back to it one
       click after "Use precise location". The colour is already in the OxiClear band
       palette (§3.4). No coordinates are kept: the place name is what the card shows. */
    publishReading(data) {
      const current = data.current || {};
      const location = data.location || {};
      const display = current.display !== undefined && current.display !== null && current.display !== ''
        ? String(current.display)
        : String(Math.round(Number(current.index)));
      const reading = {
        display,
        status: current.status || '',
        color: this.safeColor(current.color),
        place: location.name || '',
        source: location.source === 'precise' || location.source === 'selected' ? location.source : 'approximate',
        at: Date.now()
      };
      this.writeStorage(this.readingKey, JSON.stringify(reading));
      document.dispatchEvent(new CustomEvent('oxiclear:air-reading', { detail: reading }));
    }

    async fetchReading(params, bypass) {
      const url = this.makeUrl(params);
      const key = url.toString();
      const cached = this.resultCache.get(key);
      if (!bypass && cached && cached.expiresAt > Date.now()) return cached.data;
      if (!bypass && this.inflight.has(key)) return this.inflight.get(key);
      if (this.dataController) this.dataController.abort();
      const controller = new AbortController();
      this.dataController = controller;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 12000);
      const promise = fetch(url, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal
      }).then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const error = new Error(payload && payload.error ? payload.error : this.root.dataset.copyServiceUnavailable);
          error.code = payload && payload.code ? payload.code : '';
          throw error;
        }
        if (
          !payload ||
          !payload.location ||
          !payload.current ||
          !Number.isFinite(Number(payload.current.index))
        ) {
          const error = new Error(this.root.dataset.copyServiceUnavailable);
          error.code = 'INVALID_RESPONSE';
          throw error;
        }
        this.resultCache.set(key, { data: payload, expiresAt: Date.now() + 5 * 60 * 1000 });
        return payload;
      }).catch((error) => {
        if (timedOut) {
          const timeoutError = new Error(this.root.dataset.copyServiceUnavailable);
          timeoutError.code = 'TIMEOUT';
          throw timeoutError;
        }
        throw error;
      }).finally(() => {
        window.clearTimeout(timeoutId);
        if (this.dataController === controller) this.dataController = null;
        if (this.inflight.get(key) === promise) this.inflight.delete(key);
      });
      this.inflight.set(key, promise);
      return promise;
    }

    async refresh() {
      if (!this.activeRequest) {
        await this.loadReading({ type: 'approximate' }, {
          message: this.root.dataset.copyRefreshing,
          bypass: true
        });
        return;
      }
      if (this.activeRequest.type === 'precise') {
        await this.usePreciseLocation(true);
        return;
      }
      await this.loadReading(this.activeRequest, {
        message: this.root.dataset.copyRefreshing,
        bypass: true,
        retainDuringLoad: true,
        refresh: true
      });
    }

    prepareLoading(request) {
      this.root.style.setProperty('--aircheck-status', '#C1C1C2');
      this.root.style.setProperty('--aircheck-marker', '0%');
      this.locationSource.textContent = this.sourceLabel(request.type);
      this.preciseAction.textContent = request.type === 'precise'
        ? this.root.dataset.labelPreciseActive
        : this.root.dataset.labelPrecise;
      this.place.textContent = request.type === 'selected'
        ? this.root.dataset.copyFindingReading
        : this.root.dataset.copyFindingLocation;
      this.region.textContent = this.root.dataset.copyFindingReading;
      this.indexName.textContent = this.root.dataset.copyWaiting;
      this.indexValue.textContent = '—';
      this.status.textContent = this.root.dataset.copyWaiting;
      this.meaning.textContent = this.root.dataset.copyFindingReading;
      this.updated.textContent = '—';
      this.updatedLabel.textContent = this.updatedDefaultLabel;
      this.dominantName.textContent = '—';
      this.dominantValue.textContent = '—';
      this.dominantUnit.textContent = '';
      this.scale.hidden = true;
      this.pollutants.replaceChildren();
      this.clearHourly();
      this.clearHistory();
      this.hidePollen();
      this.currentLocation = null;
      this.updateFavoriteButton();
    }

    showError(message) {
      this.setBusy(false, 'error');
      this.place.textContent = this.root.dataset.copyReadingUnavailable;
      this.region.textContent = message;
      this.indexValue.textContent = '—';
      this.status.textContent = this.root.dataset.copyWaiting;
      this.meaning.textContent = message;
      this.updated.textContent = '—';
      this.setNotice(message);
      this.refreshButton.hidden = false;
      this.announce(message);
    }

    render(data) {
      const location = data.location || {};
      const current = data.current || {};
      const color = this.safeColor(current.color) || '#C1C1C2';
      this.root.style.setProperty('--aircheck-status', color);
      /* The CPCB band key for the CURRENT reading. data-air-band already exists
         but buckets the six bands into four tiers by hue, so it cannot tell
         Moderate from Satisfactory, or Very Poor from Severe. The section CSS
         needs all six. Additive: nothing existing is renamed or removed. */
      const currentBand = OXI_BAND_PALETTE[String(current.color || '').toLowerCase()];
      this.root.dataset.airBandKey = currentBand ? currentBand.key : '';
      this.currentLocation = location;
      this.renderLocationPresentation(location);
      this.place.textContent = location.name || this.root.dataset.copyReadingUnavailable;
      this.region.textContent = this.regionLabel(location);
      this.indexName.textContent = current.indexName || current.indexCode || this.root.dataset.copyWaiting;
      this.status.textContent = current.status || this.root.dataset.copyWaiting;
      this.meaning.textContent = current.message || (current.status ? current.status + '.' : this.root.dataset.copyReadingUnavailable);
      this.updated.textContent = current.updated || '—';
      this.updated.dateTime = current.updatedAt || '';
      this.updatedLabel.textContent = this.root.dataset.copyUpdated || this.updatedDefaultLabel;
      const displayValue = current.display !== undefined && current.display !== null && current.display !== ''
        ? current.display
        : current.index;
      this.indexValue.setAttribute(
        'aria-label',
        [current.indexName || current.indexCode, displayValue, current.status]
          .filter((value) => value !== undefined && value !== null && value !== '')
          .join(', ')
      );
      this.animateIndex(Number(current.index), String(displayValue));
      this.renderDominant(current.dominant);
      this.renderScale(current.scale, Number(current.index), current.status, current.display);
      this.renderHourly(data.hourlyForecast, data.hourlyForecastMeta);
      this.renderDaily(data.history, data.forecast, data.trendMeta);
      this.renderPollutants(current.pollutants);
      this.renderPollen(data.pollen, data.attribution);
      this.renderFavorites();
      if (data.attribution && data.attribution.airQuality) {
        this.airAttribution.textContent = data.attribution.airQuality;
      }
      /* --- Horizon Dial additions (additive only) --------------------------
         Derives the living-sky band and the dial fill angle for the Horizon
         Dial presentation from data already available above (current.index,
         current.color, current.scale). No existing behaviour is changed. */
      const dialIndexValue = Number(current.index);
      const dialBands = current.scale && Array.isArray(current.scale.bands) ? current.scale.bands : [];
      let dialProgress = null;
      if (dialBands.length && Number.isFinite(dialIndexValue)) {
        const dialUppers = dialBands.map((band) => band.max === null ? band.min : band.max);
        const dialMax = Math.max(Number(current.scale.max), ...dialUppers.map(Number));
        const dialMin = Number(current.scale.min);
        if (Number.isFinite(dialMin) && Number.isFinite(dialMax) && dialMax > dialMin) {
          dialProgress = (Math.min(dialMax, Math.max(dialMin, dialIndexValue)) - dialMin) / (dialMax - dialMin);
        }
      }
      if (dialProgress === null && Number.isFinite(dialIndexValue)) {
        dialProgress = Math.min(1, Math.max(0, dialIndexValue / 500));
      }
      this.root.style.setProperty(
        '--aircheck-dial-angle',
        (Math.round((dialProgress === null ? 0 : dialProgress) * 292 * 10) / 10) + 'deg'
      );
      let dialBandTier = null;
      if (dialBands.length > 1 && Number.isFinite(dialIndexValue)) {
        const dialBandIndex = dialBands.findIndex((band) =>
          dialIndexValue >= Number(band.min) && (band.max === null || dialIndexValue <= Number(band.max)));
        if (dialBandIndex >= 0) dialBandTier = dialBandIndex / (dialBands.length - 1);
      }
      if (dialBandTier === null) {
        const dialHex = /^#([0-9a-f]{6})$/i.exec(color || '');
        if (dialHex) {
          const dialR = parseInt(dialHex[1].slice(0, 2), 16);
          const dialG = parseInt(dialHex[1].slice(2, 4), 16);
          const dialB = parseInt(dialHex[1].slice(4, 6), 16);
          const dialMaxChannel = Math.max(dialR, dialG, dialB);
          const dialMinChannel = Math.min(dialR, dialG, dialB);
          let dialHue = 0;
          if (dialMaxChannel !== dialMinChannel) {
            if (dialMaxChannel === dialR) {
              dialHue = (60 * ((dialG - dialB) / (dialMaxChannel - dialMinChannel)) + 360) % 360;
            } else if (dialMaxChannel === dialG) {
              dialHue = 60 * ((dialB - dialR) / (dialMaxChannel - dialMinChannel)) + 120;
            } else {
              dialHue = 60 * ((dialR - dialG) / (dialMaxChannel - dialMinChannel)) + 240;
            }
          }
          dialBandTier = dialHue >= 130 && dialHue < 300 ? 0 : dialHue >= 70 ? 0.3 : dialHue >= 40 ? 0.45 : dialHue >= 20 ? 0.6 : 1;
        } else {
          dialBandTier = 0.3;
        }
      }
      this.root.dataset.airBand = dialBandTier < 0.2 ? 'good' : dialBandTier < 0.5 ? 'fair' : dialBandTier < 0.75 ? 'poor' : 'severe';
      /* --- end Horizon Dial additions ------------------------------------- */
    }

    renderDominant(dominant) {
      if (!dominant || !dominant.available) {
        this.dominantName.textContent = '—';
        this.dominantValue.textContent = '—';
        this.dominantUnit.textContent = '';
        return;
      }
      this.dominantName.textContent = dominant.label || dominant.key || '—';
      this.dominantValue.textContent = this.number(dominant.concentration);
      this.dominantUnit.textContent = dominant.unit || '';
    }

    renderScale(scale, currentIndex, status, currentDisplay) {
      if (!scale || !Array.isArray(scale.bands) || !scale.bands.length) {
        this.scale.hidden = true;
        return;
      }
      this.scale.hidden = false;
      this.scaleName.textContent = scale.name || scale.code || '';
      const displayValue = currentDisplay || this.number(currentIndex);
      this.scaleCurrent.textContent = displayValue + (status ? ' · ' + status : '');
      this.scaleMin.textContent = scale.displayMin || String(scale.min);
      this.scaleMax.textContent = scale.displayMax || String(scale.max);
      this.scaleTrack.querySelectorAll('[data-air-band]').forEach((band) => band.remove());
      const upperValues = scale.bands.map((band) => band.max === null ? band.min : band.max);
      const effectiveMax = Math.max(Number(scale.max), ...upperValues.map(Number));
      const effectiveMin = Number(scale.min);
      const span = Math.max(1, effectiveMax - effectiveMin + 1);
      const descriptions = [];
      scale.bands.forEach((band) => {
        const upper = band.max === null ? effectiveMax : Number(band.max);
        const width = Math.max(1, upper - Number(band.min) + 1) / span * 100;
        const element = document.createElement('span');
        element.className = 'oc-aircheck__scale-band';
        element.dataset.airBand = band.key || '';
        element.style.setProperty('--band-color', this.safeColor(band.color) || '#C1C1C2');
        element.style.width = width + '%';
        element.title = band.label + ' ' + band.min + '–' + (band.max === null ? scale.displayMax : band.max);
        element.setAttribute('aria-hidden', 'true');
        this.scaleTrack.insertBefore(element, this.scaleMarker);
        descriptions.push(element.title);
      });
      const progress = (Math.min(effectiveMax, Math.max(effectiveMin, currentIndex)) - effectiveMin) /
        Math.max(1, effectiveMax - effectiveMin) * 100;
      this.root.style.setProperty('--aircheck-marker', progress + '%');
      this.scale.setAttribute(
        'aria-label',
        (scale.name || scale.code || 'Local index') + ': ' + descriptions.join(', ') +
        '. Current value ' + displayValue + (status ? ', ' + status : '')
      );
    }

    renderHourly(entries, meta) {
      this.clearHourly();
      const hourly = Array.isArray(entries)
        ? entries.filter((entry) => Number.isFinite(Number(entry.index))).slice(0, 12)
        : [];
      if (!meta || !meta.available || !hourly.length) {
        this.hourlyState.hidden = false;
        this.hourlyState.textContent = this.root.dataset.copyHourlyUnavailable;
        return;
      }
      this.hourlyState.hidden = true;
      this.renderHourlyCurve(hourly);
      hourly.forEach((entry) => {
        this.hourlyList.appendChild(this.createForecastItem(entry, 'hourly'));
      });
    }

    clearHourly() {
      this.hourlyList.replaceChildren();
      this.hourlyState.hidden = true;
      this.clearHourlyCurve();
    }

    renderHourlyCurve(entries) {
      if (!this.hourlyCurve) return;
      const values = Array.isArray(entries)
        ? entries.map((entry) => Number(entry.index)).filter(Number.isFinite).slice(0, 12)
        : [];
      if (values.length < 2) {
        this.clearHourlyCurve();
        return;
      }

      const namespace = 'http://www.w3.org/2000/svg';
      const width = 660;
      const height = 120;
      const paddingX = 12;
      const paddingY = 12;
      const minimum = Math.min(...values);
      const maximum = Math.max(...values);
      const span = maximum - minimum;
      const step = (width - paddingX * 2) / Math.max(1, values.length - 1);
      const points = values.map((value, index) => ({
        x: paddingX + step * index,
        y: span > 0
          ? paddingY + (maximum - value) / span * (height - paddingY * 2)
          : height / 2
      }));
      const lineData = points.slice(1).reduce(
        (path, point) => path + ' L ' + point.x + ' ' + point.y,
        'M ' + points[0].x + ' ' + points[0].y
      );
      const baseline = height - paddingY;

      this.hourlyCurve.replaceChildren();
      this.hourlyCurve.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
      this.hourlyCurve.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      this.hourlyCurve.setAttribute('aria-hidden', 'true');
      this.hourlyCurve.setAttribute('focusable', 'false');

      const area = document.createElementNS(namespace, 'path');
      area.setAttribute('class', 'oc-aircheck__hourly-area');
      area.setAttribute(
        'd',
        lineData + ' L ' + points[points.length - 1].x + ' ' + baseline +
        ' L ' + points[0].x + ' ' + baseline + ' Z'
      );
      const line = document.createElementNS(namespace, 'path');
      line.setAttribute('class', 'oc-aircheck__hourly-line');
      line.setAttribute('d', lineData);
      this.hourlyCurve.append(area, line);

      points.forEach((point, index) => {
        const marker = document.createElementNS(namespace, 'circle');
        marker.setAttribute('class', 'oc-aircheck__hourly-point');
        marker.setAttribute('cx', String(point.x));
        marker.setAttribute('cy', String(point.y));
        marker.setAttribute('r', '3');
        marker.style.setProperty(
          '--point-color',
          this.safeColor(entries[index] && entries[index].color) || '#C1C1C2'
        );
        this.hourlyCurve.appendChild(marker);
      });
      this.hourlyCurve.removeAttribute('hidden');
    }

    clearHourlyCurve() {
      if (!this.hourlyCurve) return;
      this.hourlyCurve.replaceChildren();
      this.hourlyCurve.setAttribute('hidden', '');
    }

    renderDaily(historyEntries, forecastEntries, meta) {
      this.clearHistory();
      const past = Array.isArray(historyEntries)
        ? historyEntries.filter((entry) => Number.isFinite(Number(entry.index))).slice(-4)
        : [];
      const forecast = Array.isArray(forecastEntries)
        ? forecastEntries.filter((entry) => Number.isFinite(Number(entry.index))).slice(0, 4)
        : [];
      if (!past.length && !forecast.length) {
        this.historyContent.hidden = true;
        this.historyState.hidden = false;
        this.historyState.textContent = this.root.dataset.copyHistoryUnavailable;
        return;
      }
      this.historyContent.hidden = false;
      this.historyState.hidden = !(meta && meta.partial);
      this.historyState.textContent = meta && meta.partial
        ? this.root.dataset.copyOutlookPartial
        : this.root.dataset.copyHistoryUnavailable;
      this.historyMethod.textContent = meta && meta.methodLabel ? meta.methodLabel : '';
      past.forEach((entry) => this.pastList.appendChild(this.createForecastItem(entry, 'past')));
      forecast.forEach((entry) => this.forecastList.appendChild(this.createForecastItem(entry, 'forecast')));
    }

    createForecastItem(entry, kind) {
      const item = document.createElement('li');
      item.className = 'oc-aircheck__forecast-item is-' + kind;
      const rawLabel = kind === 'hourly'
        ? (entry.time || entry.label || '')
        : (entry.label || entry.date || '');
      const label = document.createElement('time');
      label.textContent = kind === 'hourly' ? this.compactHourLabel(rawLabel) : rawLabel;
      if (kind === 'hourly' && rawLabel) label.title = rawLabel;
      if (kind !== 'hourly' && entry.date) label.dateTime = entry.date;
      const dot = document.createElement('i');
      dot.setAttribute('aria-hidden', 'true');
      dot.style.setProperty('--point-color', this.safeColor(entry.color) || '#C1C1C2');
      const value = document.createElement('strong');
      value.textContent = String(entry.display || Math.round(Number(entry.index)));
      const status = document.createElement('small');
      status.textContent = entry.status || '';
      item.setAttribute('aria-label', [rawLabel, entry.indexName, value.textContent, status.textContent].filter(Boolean).join(', '));
      item.append(label, dot, value, status);
      return item;
    }

    compactHourLabel(value) {
      const label = String(value || '').trim();
      const twelveHour = label.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i);
      if (twelveHour) {
        const minutes = twelveHour[2] && twelveHour[2] !== '00' ? ':' + twelveHour[2] : '';
        return twelveHour[1] + minutes + twelveHour[3].slice(0, 1).toLowerCase();
      }
      return label.replace(/\s+[A-Z]{2,5}$/, '');
    }

    clearHistory() {
      this.pastList.replaceChildren();
      this.forecastList.replaceChildren();
      this.historyState.hidden = true;
    }

    renderPollutants(pollutants) {
      this.pollutants.replaceChildren();
      Object.values(pollutants || {}).forEach((pollutant) => {
        const article = document.createElement('article');
        article.className = 'oc-aircheck__pollutant';
        const label = document.createElement('span');
        label.textContent = pollutant.label || pollutant.key || '';
        const value = document.createElement('strong');
        value.textContent = pollutant.available
          ? this.number(pollutant.concentration)
          : this.root.dataset.copyPollutantUnavailable;
        const unit = document.createElement('small');
        unit.textContent = pollutant.available
          ? (pollutant.unit || this.root.dataset.copyCurrentConcentration)
          : '';
        article.append(label, value, unit);
        this.pollutants.appendChild(article);
      });
    }

    renderPollen(pollen, attribution) {
      if (!pollen || !pollen.available || !pollen.today) {
        this.hidePollen();
        return;
      }
      this.pollenSection.hidden = false;
      this.pollenSummary.replaceChildren();
      const today = pollen.today;
      const cards = [
        {
          label: this.root.dataset.copyPollenOverall,
          value: today.overall && today.overall.value,
          category: today.overall && today.overall.category,
          color: today.overall && today.overall.color
        },
        {
          label: this.root.dataset.copyPollenLeading,
          value: today.leadingType && today.leadingType.displayName,
          category: today.leadingType && today.leadingType.index && today.leadingType.index.category,
          color: today.leadingType && today.leadingType.index && today.leadingType.index.color
        }
      ];
      ['TREE', 'GRASS', 'WEED'].forEach((code) => {
        const type = Array.isArray(today.types) ? today.types.find((item) => item.code === code) : null;
        const fallbackLabel = {
          TREE: this.root.dataset.copyPollenTree,
          GRASS: this.root.dataset.copyPollenGrass,
          WEED: this.root.dataset.copyPollenWeed
        }[code];
        cards.push({
          label: type && type.displayName ? type.displayName : fallbackLabel,
          value: type && type.index ? type.index.value : null,
          category: type && type.index ? type.index.category : this.root.dataset.copyPollenNotReported,
          color: type && type.index ? type.index.color : null
        });
      });
      cards.forEach((card) => {
        const article = document.createElement('article');
        article.className = 'oc-aircheck__pollen-card';
        article.style.setProperty('--pollen-color', this.safeColor(card.color) || '#D6D6D6');
        const label = document.createElement('span');
        label.textContent = card.label;
        const value = document.createElement('strong');
        value.textContent = card.value === null || card.value === undefined || card.value === ''
          ? this.root.dataset.copyPollenNotReported
          : String(card.value);
        const category = document.createElement('small');
        category.textContent = card.category || '';
        article.append(label, value, category);
        this.pollenSummary.appendChild(article);
      });
      const futureDays = Array.isArray(pollen.days) ? pollen.days.slice(1) : [];
      this.pollenDays.replaceChildren();
      this.pollenForecast.hidden = futureDays.length === 0;
      if (futureDays.length) {
        const wrap = document.createElement('div');
        wrap.className = 'oc-aircheck__pollen-days';
        futureDays.forEach((day) => {
          const row = document.createElement('div');
          const leading = day.leadingType;
          const leadingIndex = leading && leading.index;
          row.textContent = day.date + ' · ' +
            (leading && leadingIndex
              ? leading.displayName + ' ' + leadingIndex.category
              : this.root.dataset.copyPollenNotReported);
          wrap.appendChild(row);
        });
        this.pollenDays.appendChild(wrap);
      }
      this.pollenAttribution.textContent = attribution && attribution.pollen
        ? attribution.pollen
        : this.root.dataset.copyPollenSource;
      this.pollenAttribution.hidden = false;
      this.pollenAttributionSeparator.hidden = false;
    }

    hidePollen() {
      this.pollenSection.hidden = true;
      this.pollenSummary.replaceChildren();
      this.pollenDays.replaceChildren();
      this.pollenForecast.hidden = true;
      this.pollenAttribution.textContent = '';
      this.pollenAttribution.hidden = true;
      this.pollenAttributionSeparator.hidden = true;
    }

    animateIndex(value, fallback) {
      if (this.numberFrame) cancelAnimationFrame(this.numberFrame);
      if (!Number.isFinite(value) || this.reducedMotion) {
        this.indexValue.textContent = fallback;
        return;
      }
      const start = performance.now();
      const duration = 520;
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.indexValue.textContent = String(Math.round(value * eased));
        if (progress < 1) {
          this.numberFrame = requestAnimationFrame(tick);
        } else {
          this.indexValue.textContent = fallback;
        }
      };
      this.numberFrame = requestAnimationFrame(tick);
    }

    onSearchInput() {
      const value = this.cityInput.value.trim();
      this.clearButton.hidden = value.length === 0;
      this.cityInput.setAttribute('aria-invalid', 'false');
      clearTimeout(this.suggestionTimer);
      if (value.length < 2) {
        this.hideSuggestions();
        return;
      }
      this.suggestionTimer = setTimeout(() => this.loadSuggestions(value), 260);
    }

    async loadSuggestions(input) {
      if (this.suggestionController) this.suggestionController.abort();
      const controller = new AbortController();
      this.suggestionController = controller;
      this.cityInput.setAttribute('aria-busy', 'true');
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 8000);
      const url = this.makeUrl({
        action: 'autocomplete',
        input,
        sessionToken: this.sessionToken
      });
      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || this.root.dataset.copySearchError);
        if (input !== this.cityInput.value.trim()) return;
        this.suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
        this.activeSuggestion = -1;
        this.renderSuggestions();
        if (this.suggestions.length) {
          this.cityInput.setAttribute('aria-invalid', 'false');
          this.announce(this.root.dataset.copySearchResults + ' ' + this.suggestions.length + '.');
        } else {
          this.cityInput.setAttribute('aria-invalid', 'true');
          this.setNotice(this.root.dataset.copySearchEmpty);
        }
      } catch (error) {
        if (error && error.name === 'AbortError' && !timedOut) return;
        this.hideSuggestions();
        this.cityInput.setAttribute('aria-invalid', 'true');
        this.setNotice(this.root.dataset.copySearchError);
      } finally {
        window.clearTimeout(timeoutId);
        if (this.suggestionController === controller) {
          this.suggestionController = null;
          this.cityInput.setAttribute('aria-busy', 'false');
        }
      }
    }

    renderSuggestions() {
      this.suggestionsList.replaceChildren();
      this.suggestions.forEach((suggestion, index) => {
        const option = document.createElement('li');
        option.id = this.suggestionsList.id + '-option-' + index;
        option.role = 'option';
        option.dataset.airOption = '';
        option.dataset.index = String(index);
        option.setAttribute('aria-selected', 'false');
        const primary = document.createElement('strong');
        primary.textContent = suggestion.primaryText || suggestion.label || '';
        const secondary = document.createElement('span');
        secondary.textContent = suggestion.secondaryText || '';
        option.append(primary, secondary);
        this.suggestionsList.appendChild(option);
      });
      const visible = this.suggestions.length > 0;
      this.suggestionsWrap.hidden = !visible;
      this.cityInput.setAttribute('aria-expanded', String(visible));
      this.cityInput.removeAttribute('aria-activedescendant');
    }

    onSearchKeydown(event) {
      if (!this.suggestions.length) {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.cityInput.setAttribute('aria-invalid', 'true');
          this.setNotice(this.root.dataset.copyInvalidSelection);
        }
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActiveSuggestion(Math.min(this.suggestions.length - 1, this.activeSuggestion + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActiveSuggestion(Math.max(0, this.activeSuggestion - 1));
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.setActiveSuggestion(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.setActiveSuggestion(this.suggestions.length - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.activeSuggestion >= 0) {
          this.selectSuggestion(this.activeSuggestion);
        } else {
          this.cityInput.setAttribute('aria-invalid', 'true');
          this.setNotice(this.root.dataset.copyInvalidSelection);
        }
      } else if (event.key === 'Escape') {
        this.hideSuggestions();
      }
    }

    setActiveSuggestion(index) {
      this.activeSuggestion = index;
      Array.from(this.suggestionsList.children).forEach((option, optionIndex) => {
        option.setAttribute('aria-selected', String(optionIndex === index));
      });
      const active = this.suggestionsList.children[index];
      if (active) {
        this.cityInput.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    async selectSuggestion(index) {
      const suggestion = this.suggestions[index];
      if (!suggestion || !this.validPlaceId(suggestion.placeId)) {
        this.cityInput.setAttribute('aria-invalid', 'true');
        this.setNotice(this.root.dataset.copyInvalidSelection);
        return;
      }
      const intent = this.beginLocationIntent();
      this.cityInput.setAttribute('aria-invalid', 'false');
      this.hideSuggestions();
      const loaded = await this.loadReading(
        { type: 'selected', placeId: suggestion.placeId, sessionToken: this.sessionToken },
        { message: this.root.dataset.copyLoadingSelected }
      );
      if (intent !== this.locationIntentSequence) return;
      if (loaded) {
        this.sessionToken = this.newSessionToken();
        this.cityInput.value = '';
        this.clearButton.hidden = true;
        this.trackAnalyticsEvent(
          'place_selected',
          this.lastData && this.lastData.analyticsLocationToken
        );
      }
    }

    clearSearch() {
      this.cityInput.value = '';
      this.clearButton.hidden = true;
      this.cityInput.setAttribute('aria-invalid', 'false');
      this.hideSuggestions();
      this.cityInput.focus();
    }

    hideSuggestions() {
      this.suggestions = [];
      this.activeSuggestion = -1;
      this.suggestionsList.replaceChildren();
      this.suggestionsWrap.hidden = true;
      this.cityInput.setAttribute('aria-expanded', 'false');
      this.cityInput.removeAttribute('aria-activedescendant');
    }

    setBusy(isBusy, state) {
      this.root.dataset.state = state;
      this.root.setAttribute('aria-busy', String(isBusy));
      this.refreshButton.disabled = isBusy;
      if (this.preciseButton.getAttribute('aria-busy') !== 'true') {
        this.preciseButton.disabled = !navigator.geolocation;
      }
    }

    setNotice(message) {
      this.notice.textContent = message || '';
      this.announce(message);
    }

    announce(message) {
      this.live.textContent = '';
      requestAnimationFrame(() => {
        this.live.textContent = message || '';
      });
    }

    sourceLabel(source) {
      if (source === 'precise') return this.root.dataset.copyLocationSourcePrecise;
      if (source === 'selected') return this.root.dataset.copyLocationSourceSelected;
      return this.root.dataset.copyLocationSourceApproximate;
    }

    sourceHelpText(source) {
      if (source === 'precise') return this.root.dataset.copySourceHelpPrecise;
      if (source === 'selected') return this.root.dataset.copySourceHelpSelected;
      return this.root.dataset.copySourceHelpApproximate;
    }

    renderLocationPresentation(location) {
      const source = location && location.source ? location.source : 'approximate';
      const rawAccuracy = location && location.accuracyMeters;
      const accuracy = Number(rawAccuracy);
      const hasAccuracy = source === 'precise'
        && rawAccuracy !== null
        && rawAccuracy !== undefined
        && rawAccuracy !== ''
        && Number.isFinite(accuracy)
        && accuracy >= 0;
      const distance = hasAccuracy ? this.formatAccuracy(accuracy) : '';
      const label = hasAccuracy
        ? this.template(this.root.dataset.copyLocationSourcePreciseAccuracy, distance)
        : location && location.sourceLabel
          ? location.sourceLabel
          : this.sourceLabel(source);
      const help = hasAccuracy
        ? this.template(this.root.dataset.copySourceHelpPreciseAccuracy, distance)
        : this.sourceHelpText(source);
      this.locationSource.textContent = label;
      this.locationSource.title = help;
      this.root.classList.toggle('oc-aircheck--precise', source === 'precise');
      this.preciseButton.classList.toggle('is-active', source === 'precise');
      this.preciseAction.textContent = source === 'precise'
        ? this.root.dataset.labelPreciseActive
        : this.root.dataset.labelPrecise;
    }

    updateLocationPrompt() {
      const choice = this.readStorage(this.promptChoiceKey);
      const collapsed = Boolean(choice);
      this.locationPrompt.classList.toggle('is-collapsed', collapsed);
      this.locationPromptCopy.hidden = collapsed;
      this.locationDismiss.hidden = collapsed;
    }

    dismissLocationPrompt(choice) {
      this.writeStorage(this.promptChoiceKey, choice || 'not-now');
      this.updateLocationPrompt();
    }

    readFavorites() {
      const raw = this.readStorage(this.favoritesKey);
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        const seen = new Set();
        return parsed.map((item) => this.normalizeFavorite(item)).filter((item) => {
          if (!item || seen.has(item.placeId)) return false;
          seen.add(item.placeId);
          return true;
        }).slice(0, 3);
      } catch {
        return [];
      }
    }

    normalizeFavorite(value) {
      if (!value || !this.validPlaceId(value.placeId)) return null;
      const clean = (input, limit = 120) => String(input || '').trim().slice(0, limit);
      const name = clean(value.name || value.label);
      const region = clean(value.region);
      const country = clean(value.country);
      const label = clean(value.label || [name, region, country].filter(Boolean).join(', '), 180);
      if (!name && !label) return null;
      return { placeId: String(value.placeId), name: name || label, region, country, label: label || name };
    }

    preferredFavoriteId() {
      const active = this.readStorage(this.activeFavoriteKey);
      if (this.favorites.some((item) => item.placeId === active)) return active;
      return this.favorites.length ? this.favorites[0].placeId : '';
    }

    persistFavorites() {
      if (this.favorites.length) {
        this.writeStorage(this.favoritesKey, JSON.stringify(this.favorites.slice(0, 3)));
      } else {
        this.removeStorage(this.favoritesKey);
      }
    }

    renderFavorites() {
      const currentPlaceId = this.currentLocation && this.currentLocation.placeId;
      const active = this.validPlaceId(currentPlaceId)
        ? currentPlaceId
        : this.readStorage(this.activeFavoriteKey);
      this.favoriteList.replaceChildren();
      this.favoriteEmpty.hidden = this.favorites.length > 0;
      if (this.favoriteCount) {
        this.favoriteCount.textContent = String(this.root.dataset.copyFavoritesCount || '')
          .replace('__COUNT__', String(this.favorites.length));
      }
      this.favorites.forEach((favorite) => {
        const item = document.createElement('li');
        const open = document.createElement('button');
        open.type = 'button';
        open.dataset.airFavoriteOpen = '';
        open.dataset.placeId = favorite.placeId;
        if (favorite.placeId === active) open.setAttribute('aria-current', 'location');
        const star = document.createElement('span');
        star.className = 'oc-aircheck__favorite-star';
        star.setAttribute('aria-hidden', 'true');
        star.textContent = '★';
        const text = document.createElement('span');
        const name = document.createElement('strong');
        name.textContent = favorite.name || favorite.label;
        const region = document.createElement('small');
        region.textContent = [favorite.region, favorite.country].filter(Boolean).join(', ');
        text.append(name, region);
        open.append(star, text);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.airFavoriteRemove = '';
        remove.dataset.placeId = favorite.placeId;
        remove.setAttribute('aria-label', this.root.dataset.copyRemoveFavorite.replace('__LOCATION__', favorite.name || favorite.label));
        remove.textContent = '×';
        item.append(open, remove);
        this.favoriteList.appendChild(item);
      });
      this.updateFavoriteButton();
    }

    onFavoriteClick(event) {
      const remove = event.target.closest('[data-air-favorite-remove]');
      if (remove) {
        this.removeFavorite(remove.dataset.placeId);
        return;
      }
      const open = event.target.closest('[data-air-favorite-open]');
      if (open) this.selectFavorite(open.dataset.placeId);
    }

    async selectFavorite(placeId) {
      if (!this.validPlaceId(placeId)) return;
      const intent = this.beginLocationIntent();
      const loaded = await this.loadReading(
        { type: 'selected', placeId },
        { message: this.root.dataset.copyLoadingSelected }
      );
      if (intent !== this.locationIntentSequence || !loaded) return;
      this.writeStorage(this.activeFavoriteKey, placeId);
      this.renderFavorites();
    }

    saveCurrentFavorite() {
      this.addFavoriteFromLocation(this.currentLocation, true);
    }

    addFavoriteFromLocation(location, announce = true) {
      const favorite = this.normalizeFavorite(location);
      if (!favorite) {
        if (announce) this.setNotice(this.root.dataset.copyFavoriteUnavailable);
        return false;
      }
      const existing = this.favorites.find((item) => item.placeId === favorite.placeId);
      if (existing) {
        this.writeStorage(this.activeFavoriteKey, existing.placeId);
        this.renderFavorites();
        if (announce) this.setNotice(this.root.dataset.copyFavoriteSaved);
        return true;
      }
      if (this.favorites.length >= 3) {
        if (announce) this.setNotice(this.root.dataset.copyFavoriteFull);
        return false;
      }
      this.favorites.push(favorite);
      this.persistFavorites();
      this.writeStorage(this.activeFavoriteKey, favorite.placeId);
      this.renderFavorites();
      if (announce) this.setNotice(this.root.dataset.copyFavoriteSaved);
      if (announce) {
        this.trackAnalyticsEvent(
          'favorite_saved',
          this.lastData && this.lastData.analyticsLocationToken
        );
      }
      return true;
    }

    removeFavorite(placeId) {
      const favorite = this.favorites.find((item) => item.placeId === placeId);
      if (!favorite) return;
      this.favorites = this.favorites.filter((item) => item.placeId !== placeId);
      if (this.readStorage(this.activeFavoriteKey) === placeId) this.removeStorage(this.activeFavoriteKey);
      this.persistFavorites();
      this.renderFavorites();
      this.setNotice(this.root.dataset.copyFavoriteRemoved);
    }

    updateFavoriteButton() {
      if (!this.favoriteButton) return;
      const placeId = this.currentLocation && this.currentLocation.placeId;
      const available = this.validPlaceId(placeId);
      const saved = available && this.favorites.some((item) => item.placeId === placeId);
      const limitReached = available && !saved && this.favorites.length >= 3;
      this.favoriteButton.hidden = false;
      this.favoriteButton.disabled = !available || saved || limitReached;
      this.favoriteButtonLabel.textContent = saved
        ? this.root.dataset.labelFavoriteSaved
        : limitReached
          ? this.root.dataset.labelFavoriteLimit
          : available
            ? this.root.dataset.labelSaveFavorite
            : this.root.dataset.labelSaveFavoriteUnavailable;
    }

    formatAccuracy(value) {
      const meters = Number(value);
      if (!Number.isFinite(meters) || meters < 0) return 'more than 500 m';
      if (meters < 1000) return Math.max(10, Math.round(meters / 10) * 10).toLocaleString() + ' m';
      const kilometers = meters / 1000;
      return kilometers.toLocaleString(undefined, { maximumFractionDigits: kilometers < 10 ? 1 : 0 }) + ' km';
    }

    template(value, distance) {
      return String(value || '').replace('__DISTANCE__', distance || '');
    }

    regionLabel(location) {
      return [location.region, location.country]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(', ');
    }

    makeUrl(params) {
      if (!this.endpoint) {
        const error = new Error(this.root.dataset.copyServiceUnavailable);
        error.code = 'MISSING_ENDPOINT';
        throw error;
      }
      const url = new URL(this.endpoint, window.location.href);
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
      });
      return url;
    }

    newSessionToken() {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      return 'oc' + Date.now().toString(36) + Math.random().toString(36).slice(2);
    }

    shouldSkipAnalytics() {
      if (window.Shopify && window.Shopify.designMode) return true;
      try {
        return new URL(window.location.href).searchParams.has('preview_theme_id');
      } catch {
        return true;
      }
    }

    initializeAnalytics() {
      if (!this.analyticsEnabled) return;
      document.addEventListener('visitorConsentCollected', this.onVisitorConsentCollected);
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            if (error || this.analyticsDestroyed) return;
            this.syncAnalyticsConsent();
          }
        );
      } catch {
        return;
      }
    }

    syncAnalyticsConsent() {
      if (!this.analyticsEnabled || this.analyticsDestroyed) return;
      let allowed = false;
      try {
        const privacy = window.Shopify && window.Shopify.customerPrivacy;
        allowed = Boolean(
          privacy &&
          typeof privacy.analyticsProcessingAllowed === 'function' &&
          privacy.analyticsProcessingAllowed()
        );
      } catch {
        allowed = false;
      }
      this.analyticsConsentReady = true;
      this.analyticsAllowed = allowed;
      if (!allowed) {
        this.removeStorage(this.analyticsVisitorKey);
        this.abortAnalyticsRequests();
        return;
      }
      this.trackWidgetReadyAnalytics();
    }

    trackWidgetReadyAnalytics() {
      if (this.analyticsWidgetReadySent || !this.lastData) return;
      const accepted = this.trackAnalyticsEvent(
        'widget_ready',
        this.lastData.analyticsLocationToken
      );
      if (accepted) this.analyticsWidgetReadySent = true;
    }

    trackAnalyticsEvent(eventType, locationToken) {
      if (
        !this.analyticsEnabled ||
        !this.analyticsConsentReady ||
        !this.analyticsAllowed ||
        this.analyticsDestroyed ||
        !['widget_ready', 'place_selected', 'favorite_saved'].includes(eventType) ||
        typeof locationToken !== 'string' ||
        !locationToken ||
        locationToken.length > 1600
      ) return false;

      const visitorId = this.analyticsVisitorId();
      const eventId = this.newAnalyticsId();
      const endpoint = this.analyticsEndpoint();
      if (!visitorId || !eventId || !endpoint) return false;

      const controller = new AbortController();
      this.analyticsControllers.add(controller);
      try {
        void fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventType, visitorId, eventId, locationToken }),
          cache: 'no-store',
          credentials: 'omit',
          keepalive: true,
          signal: controller.signal
        }).catch(() => {}).finally(() => {
          this.analyticsControllers.delete(controller);
        });
        return true;
      } catch {
        this.analyticsControllers.delete(controller);
        return false;
      }
    }

    analyticsEndpoint() {
      try {
        const dataEndpoint = new URL(this.endpoint, window.location.href);
        if (dataEndpoint.protocol !== 'https:') return '';
        return new URL('/api/air-check-events', dataEndpoint.origin).toString();
      } catch {
        return '';
      }
    }

    analyticsVisitorId() {
      if (!this.analyticsAllowed) return '';
      const existing = this.readStorage(this.analyticsVisitorKey);
      if (/^[A-Za-z0-9_-]{16,128}$/.test(existing)) return existing;
      const visitorId = this.newAnalyticsId();
      if (!visitorId) return '';
      this.writeStorage(this.analyticsVisitorKey, visitorId);
      return this.readStorage(this.analyticsVisitorKey) === visitorId ? visitorId : '';
    }

    newAnalyticsId() {
      try {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
          return window.crypto.randomUUID();
        }
        if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
          const bytes = new Uint8Array(18);
          window.crypto.getRandomValues(bytes);
          return 'oc_' + Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        }
      } catch {
        return '';
      }
      return '';
    }

    abortAnalyticsRequests() {
      this.analyticsControllers.forEach((controller) => controller.abort());
      this.analyticsControllers.clear();
    }

    validPlaceId(value) {
      return /^[A-Za-z0-9_-]{5,300}$/.test(String(value || ''));
    }

    readStorage(key) {
      try {
        return localStorage.getItem(key) || '';
      } catch {
        return '';
      }
    }

    writeStorage(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch {
        return;
      }
    }

    removeStorage(key) {
      try {
        localStorage.removeItem(key);
      } catch {
        return;
      }
    }

    destroy() {
      this.analyticsDestroyed = true;
      document.removeEventListener('visitorConsentCollected', this.onVisitorConsentCollected);
      this.abortAnalyticsRequests();
      ++this.locationIntentSequence;
      ++this.requestSequence;
      this.cancelPreciseAcquisition();
      if (this.dataController) this.dataController.abort();
      if (this.suggestionController) this.suggestionController.abort();
      if (this.suggestionTimer) window.clearTimeout(this.suggestionTimer);
      if (this.numberFrame) window.cancelAnimationFrame(this.numberFrame);
      this.tabList.removeEventListener('click', this.onTabClick);
      this.tabList.removeEventListener('keydown', this.onTabKeydown);
    }

    safeColor(value) {
      const raw = String(value || '');
      if (!/^#[0-9a-f]{6}$/i.test(raw)) return '';
      const mapped = OXI_BAND_PALETTE[raw.toLowerCase()];
      return mapped ? mapped.hex : raw;
    }

    number(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '—';
      return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
  }

  const initializeAirChecks = (scope = document) => {
    scope.querySelectorAll('[data-air-check]').forEach((root) => {
      if (root.dataset.airCheckReady) return;
      root.dataset.airCheckReady = 'true';
      root.airCheckController = new AirCheckController(root);
    });
  };

  initializeAirChecks();
  document.addEventListener('shopify:section:load', (event) => initializeAirChecks(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-air-check]').forEach((root) => {
      if (root.airCheckController) root.airCheckController.destroy();
      delete root.airCheckController;
      delete root.dataset.airCheckReady;
    });
  });
})();
